import type { AccessibilityProps } from 'react-native';

import type { SpacingSystemProps, SxStyleProp } from '@rover/kibble/core/styledSystemPropTypes';
import type {
  FontWeightProps,
  TextAlignProps,
  TextColorProps,
} from '@rover/kibble/styles/styled-system';
import type { ThemeHeadingSizes } from '@rover/kibble/styles/themeTypes';

/** Valid HTML heading tag names */
export type HeadingTags = 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/** For now, this is only props that are available on web.  Ignored on React Native. */
export type HeadingWebOnlyProps = {
  /** Visually hide heading but keep it accessible to screen readers */
  a11yHidden?: boolean;
  /** HTML element type to render as */
  as?: HeadingTags;
  /** CSS classname, web-only, AVOID USING IF YOU CAN WE HAVE OTHER METHODS OF STYLING! */
  className?: string;
  /** Enable responsive font sizing for larger heading sizes (400-600) */
  responsive?: boolean;
  /** CSS display property override */
  display?: string;
};

/** For now, this is only props that are available on Rxn.  Ignored on Web. */
export type HeadingRxnOnlyProps = AccessibilityProps;

/** Base props without the platform-specific nested props */
export type BaseHeadingProps = SpacingSystemProps &
  TextAlignProps &
  TextColorProps &
  FontWeightProps & {
    /** Heading content */
    children: React.ReactNode;
    /** Theme Heading size (50, 100, 200, 300, 400, 500, or 600). When using an array, `webProps.as` is required. */
    size?: ThemeHeadingSizes | ThemeHeadingSizes[];
    /** HTML id attribute */
    id?: string;
    /** Theme-aware custom style overrides */
    sx?:
      | SxStyleProp
      | Record<string, string | number | undefined | Array<string | number | undefined>>;
    /** ID for finding this element in unit tests */
    testID?: string;
  };

export type HeadingCommonProps = BaseHeadingProps & {
  /** Web-only props and web-specific overrides, ignored on react native */
  webProps?: Partial<Omit<BaseHeadingProps, keyof HeadingWebOnlyProps>> & HeadingWebOnlyProps;
  /** RxN-only props and rxn-specific overrides, ignored on web */
  rxnProps?: Partial<Omit<BaseHeadingProps, keyof HeadingRxnOnlyProps>> & HeadingRxnOnlyProps;
};

const MARGIN_BOTTOM_PROPS = ['m', 'margin', 'my', 'marginY', 'mb', 'marginBottom'] as const;

type MarginBottomProps = Pick<SpacingSystemProps, (typeof MARGIN_BOTTOM_PROPS)[number]>;

/**
 * A responsive `size` array makes styled-system's `variant()` expand the size
 * variant's built-in `marginBottom` into `@media` blocks, which override a
 * literal `mb` prop (that only emits a base rule). When the caller passes any
 * margin-bottom-affecting spacing prop, drop `marginBottom` from every size
 * variant so the `space` composer becomes the sole source of `margin-bottom`
 * and wins at every breakpoint. Mirrors the `fontWeight` strip in Text.tsx.
 *
 * Web-only: the React Native variants don't have this media-query cascade.
 */
export function getVariantStylesForProps<T extends Record<string, object>>(
  variants: T,
  props: MarginBottomProps
): T {
  const overridesMarginBottom = MARGIN_BOTTOM_PROPS.some((prop) => props[prop] !== undefined);
  if (!overridesMarginBottom) {
    return variants;
  }
  return Object.fromEntries(
    Object.entries(variants as Record<string, { marginBottom?: unknown }>).map(
      // marginBottom is destructured out to remove it from the returned object
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([key, { marginBottom, ...value }]) => [key, value]
    )
  ) as T;
}
